import { api } from './client'

export interface AICopilotResponse {
    status: string
    message: string
    lang: 'sw' | 'en'
    quick_topics: string[]
}

export interface AIVetDiagnosisResponse {
    success: boolean
    diagnosis: string
    animal_info?: string
    lang: 'sw' | 'en'
    provider?: string
}

export interface AIProjectionData {
    title: string
    subtitle: string
    lang: 'sw' | 'en'
    metrics: {
        current_30_production: number
        projected_30_yield: number
        yield_growth_trend: number
        current_revenue: number
        projected_revenue: number
        projected_profit: number
        projected_margin_pct: number
        feed_efficiency_score: number
        total_animals: number
    }
    recommendations: string[]
}

export interface AIInventoryReorderResponse {
    success: boolean
    report: string
    items_analyzed: Array<{
        product_name: string
        category: string
        current_stock: number
        unit: string
        sales_30_days: number
        daily_burn_rate: number
        estimated_days_left: number
        risk_level: string
    }>
    lang: 'sw' | 'en'
    provider?: string
}

export interface AIExpenseAuditResponse {
    success: boolean
    report: string
    tot_expense: number
    tot_fuel: number
    flagged_count: number
    flagged_items: Array<{
        type: string
        description: string
        amount: number
        reason: string
    }>
    lang: 'sw' | 'en'
    provider?: string
}

export interface AIFeedOptimizeResponse {
    success: boolean
    formulation: string
    livestock_type: string
    animal_count: number
    lang: 'sw' | 'en'
    provider?: string
}

export interface AIWorkforceMotivationResponse {
    success: boolean
    report: string
    worker_stats: Array<{
        name: string
        department: string
        job_title: string
        completed: number
        in_progress: number
        pending: number
        total_tasks: number
        completion_rate_pct: number
    }>
    lang: 'sw' | 'en'
    provider?: string
}

export interface AIExecutiveBriefingResponse {
    success: boolean
    briefing: string
    snapshot_summary: any
    lang: 'sw' | 'en'
    provider?: string
}

export const aiService = {
    sendCopilotMessage: async (message: string, lang?: 'sw' | 'en'): Promise<AICopilotResponse> => {
        const response = await api.post<{ status: string; message: string; lang: 'sw' | 'en'; quick_topics: string[] }>(
            'ai/copilot/chat/',
            { message, lang }
        )
        return response.data
    },

    getFarmProjections: async (lang: 'sw' | 'en' = 'en'): Promise<AIProjectionData> => {
        const response = await api.get<{ status: string; data: AIProjectionData }>('ai/projections/summary/', {
            params: { lang }
        })
        return response.data.data
    },

    diagnoseAnimalSymptoms: async (params: { animal_id?: string; species?: string; symptoms: string; lang?: 'sw' | 'en' }): Promise<AIVetDiagnosisResponse> => {
        const response = await api.post<{ status: string; data: AIVetDiagnosisResponse }>('ai/health/diagnose/', params)
        return response.data.data
    },

    getInventoryReorderAdvice: async (lang: 'sw' | 'en' = 'sw'): Promise<AIInventoryReorderResponse> => {
        const response = await api.get<{ status: string; data: AIInventoryReorderResponse }>('ai/inventory/reorder/', {
            params: { lang }
        })
        return response.data.data
    },

    auditExpenseAnomalies: async (lang: 'sw' | 'en' = 'sw'): Promise<AIExpenseAuditResponse> => {
        const response = await api.get<{ status: string; data: AIExpenseAuditResponse }>('ai/expenses/audit/', {
            params: { lang }
        })
        return response.data.data
    },

    optimizeFeedRation: async (params: { livestock_type: string; target_goal: string; animal_count: number; ingredients: string; lang?: 'sw' | 'en' }): Promise<AIFeedOptimizeResponse> => {
        const response = await api.post<{ status: string; data: AIFeedOptimizeResponse }>('ai/feed/optimize/', params)
        return response.data.data
    },

    evaluateWorkforceMotivation: async (lang: 'sw' | 'en' = 'sw'): Promise<AIWorkforceMotivationResponse> => {
        const response = await api.get<{ status: string; data: AIWorkforceMotivationResponse }>('ai/workforce/leaderboard_eval/', {
            params: { lang }
        })
        return response.data.data
    },

    getExecutiveBriefing: async (lang: 'sw' | 'en' = 'sw'): Promise<AIExecutiveBriefingResponse> => {
        const response = await api.get<{ status: string; data: AIExecutiveBriefingResponse }>('ai/executive/briefing/', {
            params: { lang }
        })
        return response.data.data
    }
}






